/**
 * lib/branding.ts
 *
 * The deployment tenant's branding lives in one place: store.branding,
 * loaded from GET /api/v1/branding/ after login (hydrateBranding). When
 * branding is null (not loaded, failed, logged out) the UI shows no airline
 * at all. The one hardcoded identity is DEMO_BRAND_IDENTITY, shown only
 * while the admin's Demo privacy mode is on.
 */
import { useMemo } from 'react';
import { useAppStore, type AppState } from '@/lib/store';
import type { Branding } from '@/lib/types';

/** The airline identity shown while Demo privacy mode is on, so a demo to
 * another airline never shows the real tenant. Operational fields (currency,
 * home base, colours) still come from the backend branding. */
export const DEMO_BRAND_IDENTITY: Pick<Branding, 'iata_code' | 'icao_code' | 'brand_name' | 'legal_name'> = {
  iata_code: 'RH',
  icao_code: 'RHA',
  brand_name: 'RH Airlines',
  legal_name: 'RH Airlines',
};

/** The branding to display: the backend branding, with the demo identity
 * swapped in when Demo privacy mode is on. null without branding. */
export function displayBranding(state: Pick<AppState, 'branding' | 'piiMaskEnabled'>): Branding | null {
  if (!state.branding) return null;
  return state.piiMaskEnabled ? { ...state.branding, ...DEMO_BRAND_IDENTITY } : state.branding;
}

/** One static logo file per deployment, named by IATA code. */
export function brandingLogoPath(branding: Pick<Branding, 'iata_code'>): string {
  return `/tenant-assets/${branding.iata_code.toLowerCase()}/logo.svg`;
}

export function useBranding(): Branding | null {
  const branding = useAppStore((s) => s.branding);
  const piiMaskEnabled = useAppStore((s) => s.piiMaskEnabled);
  return useMemo(() => displayBranding({ branding, piiMaskEnabled }), [branding, piiMaskEnabled]);
}

/** "[Brand] " for a message subject, or nothing without branding. */
export function brandSubjectPrefix(branding: Pick<Branding, 'brand_name'> | null): string {
  return branding ? `[${branding.brand_name}] ` : '';
}

/** "Brand Crew Operations", or just the team without branding. */
export function brandSignOff(branding: Pick<Branding, 'brand_name'> | null, team = 'Crew Operations'): string {
  return branding ? `${branding.brand_name} ${team}` : team;
}

/**
 * The tenant's currency, for amounts that have no currency of their own.
 * A record's own currency (booking, invoice, hotel contract) always wins;
 * this is only the fallback. null without branding -- never a literal.
 */
export function tenantCurrency(branding: Pick<Branding, 'default_currency'> | null): string | null {
  return branding?.default_currency || null;
}
